import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  LayersControl,
  useMap,
  WMSTileLayer,
} from "react-leaflet";
import { Box, IconButton, Typography, Button } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { motion, AnimatePresence } from "framer-motion";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import proj4 from "proj4";
import "./HistoricalData.css";
import Information from "./Information";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { MaterialReactTable } from 'material-react-table';
import { MapHomeControl, MapLegendControl } from "../../../common/MapControls/MapControls";

// Backend URL to fetch data from.
const backendUrl = "https://drr-dss-backend.rimes.int";

// Initial map view state for centering the map on Timor-Leste.
const INITIAL_VIEW_STATE = {
  longitude: 125.7275,
  latitude: -8.8742,
  zoom: 8,
};

proj4.defs([
  ["EPSG:32751", "+proj=utm +zone=51 +south +datum=WGS84 +units=m +no_defs"],
  ["EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs"],
]);

// Helper function to format date for the WMS layer name and API query.
const getFormattedDateForAPI = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to get yesterday's date, as that is the latest historical data available.
const getYesterdayDate = () => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  return yesterday;
};

// Define the start date for historical data.
const HISTORICAL_DATA_START_DATE_OBJ = new Date("2025-08-15T00:00:00");

// Component to update the map bounds.
const MapBoundsUpdater = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds);
    }
  }, [map, bounds]);
  return null;
};

// Helper function to map advisory text to a simplified risk level.
const mapAdvisoryToRiskLevel = (advisoryText) => {
  const advisoryMap = {
    'No Risk': 'NoRisk',
    'Low Risk': 'NoRisk',
    'Moderate Risk': 'Low',
    'Severe Risk': 'Low', 
    'High Risk': 'Medium',
    'Extreme Risk': 'High',
  };
  return advisoryMap[advisoryText] || 'N/A';
};

const HistoricalData = () => {
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [mapFooterDescription, setMapFooterDescription] = useState("Select a date to view Previous Forecast records.");
  const [loading, setLoading] = useState(false);
  const [sucoGeoJson, setSucoGeoJson] = useState(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const filterPanelRef = useRef(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');
  
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(getYesterdayDate());
  
  const [historicalData, setHistoricalData] = useState([]);
  const [sucoMap, setSucoMap] = useState(new Map());
  const geoJsonRef = useRef();

  const legendColors = useMemo(() => ({
    NoRisk: "rgb(148, 203, 85)",
    Low: "rgb(243, 235, 18)",
    Medium: "rgb(243, 155, 58)",
    High: "rgb(230, 45, 58)",
    "N/A": "rgb(222,222,223)",
    All: '#f0f0f0',
  }), []);

  // Effect to load initial static data (GeoJSON boundaries and suco names)
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [municipalityGeoResponse, sucosResponse] = await Promise.all([
          fetch("/DATA/OSMbased_adm3_boundary.json").then(res => res.json()),
          axios.post(`${backendUrl}/Municipalities/suco_list/`),
        ]);
        
        setSucoGeoJson(municipalityGeoResponse);
        if (municipalityGeoResponse && municipalityGeoResponse.features && municipalityGeoResponse.features.length > 0) {
            const geoJsonLayer = L.geoJSON(municipalityGeoResponse);
            setMapBounds(geoJsonLayer.getBounds());
        }

        const tempSucoMap = new Map();
        sucosResponse.data.forEach(s => {
          tempSucoMap.set(s.adm3_code, {
            Suco: s.adm3,
            Municipality: s.adm1,
            PostAdmin: s.adm2,
          });
        });
        setSucoMap(tempSucoMap);

      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };
    fetchInitialData();
  }, []);

  const fetchHistoricalDataByDate = useCallback(async (date) => {
    if (!date) {
      setHistoricalData([]);
      return;
    }
    setLoading(true);
    const formattedDate = getFormattedDateForAPI(date);
    try {
      const statsResponse = await axios.get(`${backendUrl}/firerisk/firerisk-stats/?date=${formattedDate}`);
      
      const socioEconomicMap = new Map(
        sucoGeoJson.features.map((feature) => [
          feature.properties.ADM_Code,
          {
            Population: feature.properties.Population || 0,
            Male: feature.properties.Male || 0,
            Female: feature.properties.Female || 0,
            HH_Urban: feature.properties.HH_Urban || 0,
            HH_Rural: feature.properties.HH_Rural || 0,
            Household: feature.properties.Household || 0,
          },
        ])
      );

      const augmentedData = statsResponse.data.map(item => {
        const names = sucoMap.get(item.adm3_code) || { Suco: 'N/A', Municipality: 'N/A', PostAdmin: 'N/A' };
        const socioData = socioEconomicMap.get(item.adm3_code) || {
          Population: 0,
          Male: 0,
          Female: 0,
          HH_Urban: 0,
          HH_Rural: 0,
          Household: 0,
        };
        const mappedAdvisory = mapAdvisoryToRiskLevel(item.advisory);
        return {
          ...item,
          ...names,
          advisory: mappedAdvisory,
          ...socioData,
        };
      });
      setHistoricalData(augmentedData);
    } catch (error) {
      console.error("Error fetching historical data:", error);
      setHistoricalData([]);
    } finally {
      setLoading(false);
    }
  }, [sucoMap, sucoGeoJson]);

  useEffect(() => {
    if (selectedCalendarDate) {
      setMapFooterDescription(`Previous Forecast records: ${getFormattedDateForAPI(selectedCalendarDate)}`);
      setSelectedLayer(`fire-risk:final_fire_risk_${getFormattedDateForAPI(selectedCalendarDate)}_masked`);
      fetchHistoricalDataByDate(selectedCalendarDate);
    } else {
      setMapFooterDescription("Select a date to view Previous Forecast records.");
      setSelectedLayer(null);
      setHistoricalData([]);
    }
  }, [selectedCalendarDate, fetchHistoricalDataByDate]);

  const handleCalendarDateChange = (date) => {
    setActiveFilter('All');
    setSelectedCalendarDate(date);
  };

  useEffect(() => {
    if (geoJsonRef.current && historicalData.length > 0) {
      geoJsonRef.current.eachLayer((layer) => {
        const feature = layer.feature;
        const { ADM_Code, ADM3 } = feature.properties;
        const dateStats = historicalData.find(item => String(item.adm3_code) === String(ADM_Code));
        
        if (dateStats) {
          let popupContent = `
            <div style="font-family: Arial, sans-serif; font-size: 12px; width: auto; max-width: 250px;">
                <h4 style="margin: 0 0 5px 0;"><strong>Suco:</strong> ${dateStats.Suco}</h4>
                <p style="margin: 0;"><strong>Municipality:</strong> ${dateStats.Municipality}</p>
                <p style="margin: 0 0 10px 0;"><strong>Post Admin:</strong> ${dateStats.PostAdmin}</p>
                <p style="margin: 0 0 5px 0;"><strong>Date:</strong> ${getFormattedDateForAPI(selectedCalendarDate)}</p>
                <p style="margin: 0 0 5px 0;"><strong>Advisory:</strong>
                               <span style="
                padding: 2px 5px; 
                border-radius: 3px; 
                font-weight: bold; 
                color: black; 
                display: inline-block;
                text-decoration: underline;
            ">
             ${dateStats.advisory}</span></p>  
                <p style="margin: 0 0 5px 0;"><strong>Total Population:</strong> ${dateStats.Population || 0}</p>
                <p style="margin: 0 0 5px 0;"><strong>Total Male:</strong> ${dateStats.Male || 0}</p>
                <p style="margin: 0 0 5px 0;"><strong>Total Female:</strong> ${dateStats.Female || 0}</p>
                <p style="margin: 0 0 5px 0;"><strong>HH Urban:</strong> ${dateStats.HH_Urban || 0}</p>
                <p style="margin: 0 0 5px 0;"><strong>HH Rural:</strong> ${dateStats.HH_Rural || 0}</p>
                <p style="margin: 0 0 5px 0;"><strong>Household:</strong> ${dateStats.Household || 0}</p>
            </div>
          `;
          layer.bindPopup(popupContent);
        } else {
          layer.bindPopup(`No data available for ${ADM3} on ${getFormattedDateForAPI(selectedCalendarDate)}`);
        }
      });
    }
  }, [historicalData, selectedCalendarDate]);

  const onEachSucoFeature = useCallback((feature, layer) => {
    const { ADM3 } = feature.properties;
    layer.bindPopup(`Loading data for ${ADM3}...`);
  }, []);

  const filteredTableData = useMemo(() => {
    if (activeFilter === 'All') {
      return historicalData;
    }
    return historicalData.filter(item => item.advisory === activeFilter);
  }, [historicalData, activeFilter]);

  const columns = useMemo(() => {
    const getAdvisoryColor = (advisory) => {
      switch (advisory) {
        case 'NoRisk': return 'rgb(148, 203, 85)';
        case 'Low': return 'rgb(243, 235, 18)';
        case 'Medium': return 'rgb(243, 155, 58)';
        case 'High': return 'rgb(230, 45, 58)';
        default: return 'rgb(222,222,223)';
      }
    };
    
    return [
      { 
        accessorKey: 'SlNo', 
        header: 'Sl No', 
        size: 50,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ row }) => row.index + 1
      },
      { accessorKey: 'Municipality', header: 'Municipality', size: 150 },
      { accessorKey: 'PostAdmin', header: 'Post Admin', size: 150 },
      { accessorKey: 'Suco', header: 'Suco', size: 150 },
      {
        accessorKey: 'advisory',
        header: 'Advisory',
        size: 100,
        Cell: ({ cell }) => (
          <span
            style={{
              backgroundColor: getAdvisoryColor(cell.getValue()),
              padding: '2px 5px',
              borderRadius: '3px',
              color: 'black',
              fontWeight: 'bold',
            }}
          >
            {cell.getValue()}
          </span>
        ),
      },
      { accessorKey: 'Population', header: 'Total Population', size: 150 },
      { accessorKey: 'Male', header: 'Total Male', size: 150 },
      { accessorKey: 'Female', header: 'Total Female', size: 150 },
      { accessorKey: 'HH_Urban', header: 'HH Urban', size: 150 },
      { accessorKey: 'HH_Rural', header: 'HH Rural', size: 150 },
      { accessorKey: 'Household', header: 'Household', size: 150 },
    ];
  }, []);

  const filterButtons = useMemo(() => {
    const riskLevels = ['All', 'NoRisk', 'Low', 'Medium', 'High'];
    return riskLevels.map(level => (
      <Button
        key={level}
        onClick={() => setActiveFilter(level)}
        sx={{
          minWidth: '80px',
          padding: '6px 12px',
          borderRadius: '20px',
          fontWeight: 'bold',
          fontSize: '14px',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          textTransform: 'none',
          ml: 1,
          mr: 1,
          backgroundColor: legendColors[level],
          color: 'black',
          border: activeFilter === level ? '2px solid black' : '2px solid transparent',
          boxShadow: activeFilter === level ? '0 0 0 2px black' : 'none',
          '&:hover': {
            backgroundColor: legendColors[level],
            opacity: 0.8,
            transform: 'translateY(-2px)',
          },
          '&.Mui-focusVisible': {
            outline: '2px solid black',
            outlineOffset: '2px',
          },
        }}
      >
        {level}
      </Button>
    ));
  }, [activeFilter, legendColors]);

  return (
    <div className="historical-data-page">
      <div className="map-container-firerisk">
        <div className="map-wrapper">
          {loading && (
            <div className="map-loading-overlay">
              <div className="spinner"></div>
              <p>Loading map layers...</p>
            </div>
          )}
          <Information />
          <MapContainer
            center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
            zoom={INITIAL_VIEW_STATE.zoom}
            className="mapcontainerall"
          >
            <MapHomeControl />
            <MapBoundsUpdater bounds={mapBounds} />
            <Box
              sx={{ position: "absolute", top: 10, left: 50, zIndex: 1000 }}
              ref={filterPanelRef}
            >
              <IconButton
                color="primary"
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                data-filter-toggle
              >
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "50%",
                    padding: "8px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: 1,
                  }}
                >
                  <FilterAltIcon sx={{ color: "#0d6efd", fontSize: 32 }} />
                </Box>
              </IconButton>

              <AnimatePresence>
                {showFilterPanel && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="FilterPanel"
                    style={{ width: 'auto' }}
                    >
                      <div className="FilterPanel-container">
                        <h4 className="dropdown-heading">Previous Forecast records:</h4>
                        <div style={{ position: 'relative', width: 'fit-content' }}>
                          <DatePicker
                            selected={selectedCalendarDate}
                            onChange={handleCalendarDateChange}
                            dateFormat="yyyy-MM-dd"
                            minDate={HISTORICAL_DATA_START_DATE_OBJ}
                            maxDate={getYesterdayDate()}
                            placeholderText="Select a date"
                            className="dropdown"
                          />
                          <CalendarTodayIcon
                            sx={{
                              position: 'absolute',
                              right: 8,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              pointerEvents: 'none',
                              color: 'rgba(0, 0, 0, 0.54)',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>

            <LayersControl position="topright">
              <LayersControl.BaseLayer name="White Theme" checked>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
              </LayersControl.BaseLayer>

              <LayersControl.Overlay name="Historical Fire Risk" checked>
                {selectedLayer && (
                  <WMSTileLayer
                    key={selectedLayer}
                    url="https://drr-dss.rimes.int/geoserver/fire-risk/wms"
                    layers={selectedLayer}
                    format="image/png"
                    transparent={true}
                    version="1.1.0"
                    opacity={0.8}
                  />
                )}
              </LayersControl.Overlay>
              
              {sucoGeoJson && (
                <LayersControl.Overlay name="Suco" checked>
                    <GeoJSON
                        ref={geoJsonRef}
                        key={getFormattedDateForAPI(selectedCalendarDate)}
                        data={sucoGeoJson}
                        style={{ color: "black", weight: 2, fillOpacity: 0 }}
                        onEachFeature={onEachSucoFeature}
                    />
                </LayersControl.Overlay>
              )}
            </LayersControl>

            <MapLegendControl
              sections={[{
                items: [
                  { label: "No-Risk", color: "rgb(148, 203, 85)" },
                  { label: "Low-Risk", color: "rgb(243, 235, 18)" },
                  { label: "Medium-Risk", color: "rgb(243, 155, 58)" },
                  { label: "High-Risk", color: "rgb(230, 45, 58)" },
                ],
              }]}
            />

            <div className="map-footer">
              <p>{mapFooterDescription}</p>
            </div>
          </MapContainer>
        </div>
      </div>
      <Box sx={{ p: 2, borderTop: '1px solid #ddd', overflow: 'auto', width: '100%' }}>
            <MaterialReactTable
              columns={columns}
              data={filteredTableData}
              enableColumnFilters
              enableColumnActions
              enableSorting
              enablePagination
              paginationDisplayMode='pages'
              enableDensityToggle={true}
              enableFullScreenToggle={true}
              enableGlobalFilter
              initialState={{
                sorting: [{ id: 'SlNo', desc: false }],
                columnVisibility: { SlNo: true },
                density: 'compact',
                pagination: { pageSize: 10 },
              }}
              state={{ isLoading: loading }}
              renderTopToolbarCustomActions={() => (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    width: '100%',
                    p: '0.5rem',
                    gap: '1rem',
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Fire Risk Data for ({selectedCalendarDate ? getFormattedDateForAPI(selectedCalendarDate) : 'Select a date'})
                  </Typography>
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                    {filterButtons}
                  </Box>
                </Box>
              )}
              muiTableContainerProps={{ sx: { maxHeight: '600px' } }}
              muiTableHeadCellProps={{
                sx: {
                  fontWeight: 'bold',
                  backgroundColor: '#e0e0e0',
                },
              }}
              renderEmptyRowsFallback={() => (
                <Typography>
                  No impact data found.
                </Typography>
              )}
            />
          </Box>
    </div>
  );
};

export default HistoricalData;